import { useState, useEffect } from "react";
import * as d3 from "d3";
import { EndNotes } from "@/components/EndNotes";
import { PageContainer } from "@/components/PageContainer";
import { Select } from "@/components/Select";
import economyOptions from "@/config/trading-partners/ economies";
import sectorOptions from "@/config/trading-partners/sectors";
import { getSectorItems, getEconomyItems } from "@/utils/filter";
import { useFilter } from "@/hooks/useFilter";
import { useUrls } from "@/hooks/useUrls";
import { PartnerEvolution } from "./PartnerEvolution";
import { ValueAdd } from "./ValueAdd";
import styles from "./styles.module.scss";
import { formatRows } from "@/utils/format";

const economyItems = getEconomyItems(economyOptions);

const sectorItems = getSectorItems(sectorOptions);

const partnerEvolutionSchema = [
  { name: "Sub-sector code" },
  {
    name: "Direction",
    format: (value: string) =>
      value.charAt(0).toUpperCase() + value.substring(1).toLowerCase(),
  },
  { name: "Partner code" },
  { name: "Partner toggle" },
  { name: "Year" },
  {
    name: "Value",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Total reporter value",
    format: (value: string) => parseInt(value),
  },
] as const;

const getPartnerEvolutionDataRows = (rows: unknown) => {
  return formatRows(rows, partnerEvolutionSchema);
};

export type PartnerEvolutionDataRow = ReturnType<
  typeof getPartnerEvolutionDataRows
>[number];

const valueAddSchema = [
  { name: "Partner code" },
  { name: "Year" },
  {
    name: "Direction",
    format: (value: string) =>
      value.charAt(0).toUpperCase() + value.substring(1).toLowerCase(),
  },
  {
    name: "Partner share",
    format: (value: string) => parseFloat(value),
  },
] as const;

const getValueAddDataRows = (rows: unknown) => {
  return formatRows(rows, valueAddSchema);
};

export type ValueAddDataRow = ReturnType<typeof getValueAddDataRows>[number];

export function App() {
  const urls = useUrls();
  const pageSlug = "trading-partners";
  const urlInfo = urls[pageSlug] || {};

  const [partnerEvolutionData, setPartnerEvolutionData] = useState<
    PartnerEvolutionDataRow[]
  >([]);
  const [isPartnerEvolutionDataLoading, setIsPartnerEvolutionDataLoading] =
    useState(true);
  const [valueAddData, setValueAddData] = useState<ValueAddDataRow[]>([]);
  const [isValueAddDataLoading, setIsValueAddDataLoading] = useState(true);

  const { value: economy, onChange: onEconomyChange } = useFilter(
    "eco",
    economyOptions
  );

  const { value: sector, onChange: onSectorChange } = useFilter(
    "sector",
    sectorOptions
  );

  useEffect(() => {
    setIsPartnerEvolutionDataLoading(true);
    d3.csv(
      `${
        process.env.ASSET_PATH || ""
      }/data/trading-partners/${economy}-${sector}.csv`
    )
      .then((data) => {
        setPartnerEvolutionData(getPartnerEvolutionDataRows(data));
      })
      .catch((e) => {
        console.error(e);
        setPartnerEvolutionData([]);
      })
      .finally(() => {
        setIsPartnerEvolutionDataLoading(false);
      });
  }, [economy, sector]);

  useEffect(() => {
    setIsValueAddDataLoading(true);
    d3.csv(
      `${
        process.env.ASSET_PATH || ""
      }/data/trading-partners-value-add/${economy}-${sector}.csv`
    )
      .then((data) => {
        setValueAddData(getValueAddDataRows(data));
      })
      .catch((e) => {
        console.error(e);
        setValueAddData([]);
      })
      .finally(() => {
        setIsValueAddDataLoading(false);
      });
  }, [economy, sector]);

  return (
    <PageContainer
      headline={urlInfo.headline}
      dek={urlInfo.dek}
      footerCTASlug="interdependencies"
      activePageSlug={pageSlug}
      isLoading={isPartnerEvolutionDataLoading || isValueAddDataLoading}
      filters={
        <>
          <Select
            label="Economy"
            items={economyItems}
            value={economy}
            format="control-bar"
            onChange={(value) => {
              onEconomyChange(value);
            }}
          />
          <Select
            label="Sector"
            items={sectorItems}
            value={sector}
            format="control-bar"
            onChange={(value) => {
              onSectorChange(value);
            }}
          />
        </>
      }
    >
      <div className={styles["partner-evolution-chart"]}>
        <PartnerEvolution
          data={partnerEvolutionData}
          economy={economy}
          sector={sector}
        />
      </div>

      <div className={styles["notes-container"]}>
        <EndNotes mode="collapsed">
          <p>
            Trade on a regional level excludes trade between countries belonging
            to the same region. Top 5 trade partners are selected based on most
            recent year of data. Trade partner value is visualized as zero for
            years that have missing data. See technical appendix for details on
            how “Not specified” trade partners are treated.
          </p>
          <p>Source: UN Comtrade (Refreshed 2025-01-30)</p>
        </EndNotes>
      </div>

      <div className={styles["value-add-chart"]}>
        <ValueAdd data={valueAddData} economy={economy} sector={sector} />
      </div>

      <div className={styles["notes-container"]}>
        <EndNotes mode="collapsed">
          <p>
            Excludes domestic value-added trade. Data not available for regional
            aggregations. Value-added is different to gross trade, which is the
            default view shown across all other visualizations. While gross
            trade accounts for the full value of a product or service each time
            it crosses borders, including the cost of inputs and the value each
            country adds to it, value-added only accounts for the latter.
          </p>
          <p>
            Source: OECD TIVA Origin of Value Added in Final Demand (Refreshed:
            2024-11-28)
          </p>
        </EndNotes>
      </div>
    </PageContainer>
  );
}
